import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, toastError } from "../../lib/api";
import { PageHeader } from "../../components/domain/Bits";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Ban, Building2, CheckCircle2, Copy, KeyRound, Play, Plus, Shield, UsersRound } from "lucide-react";
import { TENANT_TIMEZONES } from "./Tenants";
import { buildWelcomePdf } from "../../lib/welcomePdf";

type Tenant = {
  id: string; name: string; slug: string; status: string; default_timezone: string;
  applied_template?: string | null; applied_template_version?: string | null; created_at?: string;
};
type TenantUser = {
  id: string; name: string; email: string; active: boolean; role?: string | null;
  must_change_password?: boolean; last_login_at?: string | null;
};
type Welcome = {
  tenant: { id: string; name: string; slug: string };
  owner: { id: string; name: string; email: string; role: string };
  temporary_password: string; must_change_password: boolean; display_once: boolean;
  credential_issuance?: { id: string; version_number: number };
};

const TABS = ["overview", "users", "billing", "onboarding", "support", "security", "danger"] as const;
type Tab = typeof TABS[number];

function TimezoneInput({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return <div className="space-y-1">
    <Input list="tenant-360-timezones" value={value} onChange={(event) => onChange(event.target.value)}
      data-testid="tenant-360-timezone" autoComplete="off" />
    <datalist id="tenant-360-timezones">{TENANT_TIMEZONES.map((zone) =>
      <option key={zone.value} value={zone.value}>{zone.label}</option>)}</datalist>
    <p className="text-xs text-slate-500">IANA timezone with automatic daylight-saving rules.</p>
  </div>;
}

export default function TenantDetail() {
  const { tenantId } = useParams();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [tab, setTab] = useState<Tab>("overview");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [timezone, setTimezone] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [allowAdditional, setAllowAdditional] = useState(false);
  const [welcome, setWelcome] = useState<Welcome | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [tenantResponse, usersResponse] = await Promise.all([
        api.get("/platform/tenants"),
        api.get(`/platform/tenants/${tenantId}/users`),
      ]);
      const selected = tenantResponse.data.find((item: Tenant) => item.id === tenantId);
      setTenant(selected || null);
      setTimezone(selected?.default_timezone || "UTC");
      setUsers(usersResponse.data);
    } catch (error) { toastError(error); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tenantId]);

  const owners = useMemo(() => users.filter((user) => user.role === "owner_admin"), [users]);
  const onboarding = [
    { label: "Business timezone configured", done: !!tenant?.default_timezone && tenant.default_timezone !== "UTC" },
    { label: "CRM template applied", done: !!tenant?.applied_template },
    { label: "At least one Tenant Owner", done: owners.length > 0 },
    { label: "Owner completed first login", done: owners.some((owner) => !owner.must_change_password) },
  ];

  const saveTimezone = async () => {
    if (!tenant) return;
    setBusy(true);
    try {
      await api.patch(`/platform/tenants/${tenant.id}`, { default_timezone: timezone });
      toast.success("Tenant timezone updated");
      await load();
    } catch (error) { toastError(error); }
    finally { setBusy(false); }
  };

  const provisionOwner = async () => {
    if (!tenant) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/platform/tenants/${tenant.id}/owners`, {
        name: ownerName, email: ownerEmail, phone: null, allow_additional_owner: allowAdditional,
      });
      setWelcome(data);
      setOwnerName(""); setOwnerEmail(""); setAllowAdditional(false);
      await load();
    } catch (error) { toastError(error); }
    finally { setBusy(false); }
  };

  const resetOwner = async (userId: string) => {
    if (!tenant) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/platform/tenants/${tenant.id}/owners/${userId}/temporary-login`);
      setWelcome(data);
    } catch (error) { toastError(error); }
    finally { setBusy(false); }
  };

  const copyWelcome = async () => {
    if (!welcome) return;
    await navigator.clipboard.writeText([
      "RemediKeep — Welcome Login", `Tenant: ${welcome.tenant.name}`,
      `Login URL: ${window.location.origin}/login`, `Email: ${welcome.owner.email}`,
      `Temporary Password: ${welcome.temporary_password}`,
      "Password change is required on first login.",
    ].join("\n"));
    toast.success("Welcome login copied");
  };

  const downloadWelcome = async () => {
    if (!welcome) return;
    const blob = buildWelcomePdf({
      tenantName: welcome.tenant.name, tenantSlug: welcome.tenant.slug,
      ownerName: welcome.owner.name, ownerEmail: welcome.owner.email,
      temporaryPassword: welcome.temporary_password, loginUrl: `${window.location.origin}/login`,
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url; anchor.download = `${welcome.tenant.slug}-welcome-login.pdf`; anchor.click();
    URL.revokeObjectURL(url);
    if (welcome.credential_issuance) await api.post(`/platform/tenants/${welcome.tenant.id}/users/${welcome.owner.id}/login-instructions/${welcome.credential_issuance.id}/downloaded`);
    toast.success("Welcome PDF downloaded and recorded");
  };

  const changeStatus = async (action: "suspend" | "reactivate") => {
    if (!tenant) return;
    setBusy(true);
    try {
      await api.post(`/platform/tenants/${tenant.id}/${action}`);
      toast.success(action === "suspend" ? "Tenant suspended" : "Tenant reactivated");
      await load();
    } catch (error) { toastError(error); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="p-8 text-sm text-slate-500">Loading tenant workspace…</div>;
  if (!tenant) return <div className="p-8"><Link to="/platform/tenants" className="text-sm underline">Tenant not found. Return to tenants.</Link></div>;

  return <>
    <PageHeader title={tenant.name} subtitle="Tenant 360 management workspace" actions={
      <Link to="/platform/tenants"><Button variant="outline"><ArrowLeft className="mr-1 h-4 w-4" /> All tenants</Button></Link>
    } />
    <div className="mx-auto max-w-7xl space-y-6 p-6 md:p-8" data-testid="tenant-360">
      <div className="grid gap-4 sm:grid-cols-4">
        <div className="rounded-xl border bg-white p-4"><div className="text-xs text-slate-500">Status</div><div className="mt-1 font-semibold capitalize">{tenant.status}</div></div>
        <div className="rounded-xl border bg-white p-4"><div className="text-xs text-slate-500">Users</div><div className="mt-1 font-semibold">{users.length}</div></div>
        <div className="rounded-xl border bg-white p-4"><div className="text-xs text-slate-500">Template</div><div className="mt-1 font-semibold">{tenant.applied_template || "Not configured"}</div></div>
        <div className="rounded-xl border bg-white p-4"><div className="text-xs text-slate-500">Timezone</div><div className="mt-1 font-semibold">{tenant.default_timezone}</div></div>
      </div>

      <div className="overflow-x-auto border-b">
        <div className="flex min-w-max gap-1">{TABS.map((item) =>
          <button key={item} onClick={() => setTab(item)} data-testid={`tenant-tab-${item}`}
            className={`rounded-t-lg px-4 py-3 text-sm font-medium capitalize ${tab === item ? "border-b-2 border-slate-900 text-slate-900" : "text-slate-500"}`}>
            {item}
          </button>)}</div>
      </div>

      {tab === "overview" && <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-xl border bg-white p-5 space-y-4">
          <div><h2 className="font-semibold">Tenant profile</h2><p className="text-sm text-slate-500">Core workspace identity and configuration.</p></div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="text-xs text-slate-500">Name</div>{tenant.name}</div>
            <div><div className="text-xs text-slate-500">Slug</div><span className="font-mono">{tenant.slug}</span></div>
            <div><div className="text-xs text-slate-500">Status</div><span className="capitalize">{tenant.status}</span></div>
            <div><div className="text-xs text-slate-500">Template version</div>{tenant.applied_template_version || "—"}</div>
          </div>
        </section>
        <section className="rounded-xl border bg-white p-5 space-y-3">
          <div><h2 className="font-semibold">Business timezone</h2><p className="text-sm text-slate-500">Controls scheduling and calendar display for this tenant.</p></div>
          <TimezoneInput value={timezone} onChange={setTimezone} />
          <Button onClick={saveTimezone} disabled={busy || !timezone || timezone === tenant.default_timezone}>Save timezone</Button>
        </section>
        <section className="rounded-xl border border-dashed bg-slate-50 p-5"><h2 className="font-semibold">Modules & entitlements</h2><p className="mt-1 text-sm text-slate-500">Planned for the next Control Center slice. No fake enable/disable controls are exposed yet.</p></section>
        <section className="rounded-xl border bg-white p-5"><h2 className="font-semibold">Subscription & billing</h2><p className="mt-1 text-sm text-slate-500">Generate invoices, track sending, record payment, and retain the complete history.</p><Link to={`/platform/billing?tenant=${tenant.id}`}><Button className="mt-4">Open billing history</Button></Link></section>
      </div>}

      {tab === "users" && <div className="grid gap-6 lg:grid-cols-3">
        <section className="rounded-xl border bg-white lg:col-span-2">
          <div className="border-b p-5"><h2 className="font-semibold">Users & access</h2><p className="text-sm text-slate-500">All login users currently provisioned for this tenant.</p></div>
          <div className="divide-y">{users.length === 0 ? <div className="p-5 text-sm text-slate-500">No users provisioned.</div> : users.map((user) =>
            <div key={user.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div><div className="font-medium">{user.name}</div><div className="text-sm text-slate-500">{user.email} · {user.role || "No role"} · {user.active ? "Active" : "Inactive"}</div>
                <div className="text-xs text-slate-400">{user.last_login_at ? `Last login: ${new Date(user.last_login_at).toLocaleString()}` : "Never logged in"}</div></div>
              {user.role === "owner_admin" && <Button variant="outline" size="sm" onClick={() => resetOwner(user.id)} disabled={busy}><KeyRound className="mr-1 h-4 w-4" /> New temporary login</Button>}
            </div>)}</div>
        </section>
        <section className="rounded-xl border bg-white p-5 space-y-3">
          <div><h2 className="font-semibold"><Plus className="mr-1 inline h-4 w-4" /> Add Tenant Owner</h2><p className="text-sm text-slate-500">Creates a one-time welcome login.</p></div>
          <div><Label>Full name</Label><Input value={ownerName} onChange={(event) => setOwnerName(event.target.value)} data-testid="tenant-360-owner-name" /></div>
          <div><Label>Email</Label><Input type="email" value={ownerEmail} onChange={(event) => setOwnerEmail(event.target.value)} data-testid="tenant-360-owner-email" /></div>
          {owners.length > 0 && <label className="flex gap-2 text-sm"><input type="checkbox" checked={allowAdditional} onChange={(event) => setAllowAdditional(event.target.checked)} /> I intentionally want another Tenant Owner.</label>}
          <Button onClick={provisionOwner} disabled={busy || tenant.status !== "active" || ownerName.trim().length < 2 || !ownerEmail || (owners.length > 0 && !allowAdditional)}>Generate welcome login</Button>
        </section>
        {welcome && <section className="rounded-xl border border-amber-200 bg-amber-50 p-5 lg:col-span-3" data-testid="tenant-360-welcome">
          <h2 className="font-semibold">One-time welcome login</h2><p className="text-sm text-amber-900">Copy or download now. The password disappears when this page is closed or refreshed.</p>
          <div className="mt-3 rounded-lg border bg-white p-4 font-mono text-sm">{welcome.owner.email}<br />{welcome.temporary_password}</div>
          <div className="mt-3 flex gap-2"><Button variant="outline" onClick={downloadWelcome}>Download PDF</Button><Button onClick={copyWelcome}><Copy className="mr-1 h-4 w-4" /> Copy login</Button></div>
        </section>}
      </div>}

      {tab === "billing" && <section className="rounded-xl border bg-white p-5">
        <h2 className="font-semibold">Invoices & payment history</h2>
        <p className="mt-1 text-sm text-slate-500">Create a branded invoice PDF, mark it sent, receive due and overdue reminders, and record payment without losing the timeline.</p>
        <Link to={`/platform/billing?tenant=${tenant.id}`}><Button className="mt-4">Manage {tenant.name} billing</Button></Link>
      </section>}

      {tab === "onboarding" && <section className="rounded-xl border bg-white">
        <div className="border-b p-5"><h2 className="font-semibold">Onboarding readiness</h2><p className="text-sm text-slate-500">Platform-visible setup checks based on current tenant data.</p></div>
        <div className="divide-y">{onboarding.map((item) => <div key={item.label} className="flex items-center justify-between p-4"><span>{item.label}</span><span className={item.done ? "text-emerald-700" : "text-amber-700"}>{item.done ? "Complete" : "Needs attention"}</span></div>)}</div>
      </section>}

      {tab === "support" && <section className="rounded-xl border bg-white p-5">
        <Shield className="h-6 w-6" /><h2 className="mt-3 font-semibold">Audited support access</h2><p className="mt-1 text-sm text-slate-500">Create a time-limited support session from the Platform Support Access console and select {tenant.name}.</p>
        <Link to="/platform/support-access"><Button className="mt-4">Open Support Access</Button></Link>
      </section>}

      {tab === "security" && <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-xl border bg-white p-5"><UsersRound className="h-5 w-5" /><h2 className="mt-3 font-semibold">Access summary</h2><p className="mt-2 text-sm">{users.filter((user) => user.active).length} active users · {owners.length} Tenant Owner{owners.length === 1 ? "" : "s"}</p></section>
        <section className="rounded-xl border bg-white p-5"><CheckCircle2 className="h-5 w-5" /><h2 className="mt-3 font-semibold">Audit boundary</h2><p className="mt-2 text-sm text-slate-500">Password resets, timezone changes, lifecycle actions, and support sessions remain server-authorized and auditable.</p></section>
      </div>}

      {tab === "danger" && <section className="rounded-xl border border-rose-200 bg-white p-5">
        <h2 className="font-semibold text-rose-800">Danger Zone</h2><p className="mt-1 text-sm text-slate-600">Suspension disables tenant access without deleting client records.</p>
        {tenant.status === "active" ? <Button variant="destructive" className="mt-4" disabled={busy} onClick={() => changeStatus("suspend")}><Ban className="mr-1 h-4 w-4" /> Suspend tenant</Button>
          : <Button className="mt-4" disabled={busy} onClick={() => changeStatus("reactivate")}><Play className="mr-1 h-4 w-4" /> Reactivate tenant</Button>}
      </section>}
    </div>
  </>;
}
